/**
 * CalendarSlidePanel Component
 * Slide-in panel showing week calendar with subcontractor availability
 * Used on AssignmentDetail page for planning
 */

import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  UserPlus,
  Filter,
  Check,
  UserCheck
} from 'lucide-react'
import {
  format,
  addDays,
  startOfWeek,
  addWeeks,
  subWeeks,
  isSameDay,
  isToday,
  getWeek
} from 'date-fns'
import { sv } from 'date-fns/locale'
import { supabase } from '../../lib/supabase'
import { getHolidayForDate } from '../../utils/swedishHolidays'
import { isDateInUnavailabilityPeriod, type Unavailability } from '../../services/unavailability'

interface CalendarSlidePanelProps {
  isOpen: boolean
  onClose: () => void
  assignmentId: string
  assignmentStatus: string
  currentDate: string | null
  onDateChange: (date: string, requiresConfirmation: boolean) => void
  assignedSubcontractorIds: string[]
  onAddSubcontractor: (subcontractor: { id: string; name: string; phone: string | null }) => void
  assignmentTitle: string
}

interface AllSubcontractor {
  id: string
  name: string
  color: string
  phone: string | null
  available_days: boolean[] | null  // [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
}

interface SubcontractorAssignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  start_time: string | null
  end_time: string | null
  estimated_hours: number | null
  status: string
}

interface Subcontractor {
  id: string
  name: string
  color: string
  phone: string | null
  available_days: boolean[] | null  // [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
  assignments: SubcontractorAssignment[]
}

// Statuses that require confirmation modal
const PLANNED_STATUSES = ['planerad', 'pagaende', 'bekraftad', 'avslutad']

export function CalendarSlidePanel({
  isOpen,
  onClose,
  assignmentId,
  assignmentStatus,
  currentDate,
  onDateChange,
  assignedSubcontractorIds,
  onAddSubcontractor,
  assignmentTitle
}: CalendarSlidePanelProps) {
  // Initialize week to show the assignment's date, or current week
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    if (currentDate) {
      return startOfWeek(new Date(currentDate), { weekStartsOn: 1 })
    }
    return startOfWeek(new Date(), { weekStartsOn: 1 })
  })

  // TG filter state - defaults to assigned TGs only
  const [selectedSubcontractorIds, setSelectedSubcontractorIds] = useState<string[]>(assignedSubcontractorIds)
  const [showTgFilter, setShowTgFilter] = useState(false)

  // Update selected TGs when assigned TGs change
  useEffect(() => {
    if (isOpen) {
      setSelectedSubcontractorIds(assignedSubcontractorIds)
    }
  }, [assignedSubcontractorIds, isOpen])

  // Update week when currentDate changes
  useEffect(() => {
    if (currentDate && isOpen) {
      setCurrentWeekStart(startOfWeek(new Date(currentDate), { weekStartsOn: 1 }))
    }
  }, [currentDate, isOpen])

  // Calculate week days
  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i)),
    [currentWeekStart]
  )

  const weekNumber = getWeek(currentWeekStart, { weekStartsOn: 1 })

  // Check if status requires confirmation
  const isPastPreliminary = PLANNED_STATUSES.includes(assignmentStatus)

  // Fetch all active subcontractors for the filter (pre-fetch when panel opens)
  const { data: allSubcontractors = [], isLoading: isLoadingAllTg } = useQuery({
    queryKey: ['all-subcontractors-for-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subcontractors')
        .select('id, name, color, phone, available_days')
        .eq('is_active', true)
        .order('name')

      if (error) throw error
      return data as AllSubcontractor[]
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  })

  // Fetch subcontractors with their assignments for this week
  const { data: subcontractors = [], isLoading } = useQuery({
    queryKey: ['calendar-panel-subcontractors', selectedSubcontractorIds, currentWeekStart.toISOString()],
    queryFn: async () => {
      if (selectedSubcontractorIds.length === 0) return []

      const startDate = format(currentWeekStart, 'yyyy-MM-dd')
      const endDate = format(addDays(currentWeekStart, 6), 'yyyy-MM-dd')

      // Fetch subcontractors
      const { data: subData, error: subError } = await supabase
        .from('subcontractors')
        .select('id, name, color, phone, available_days')
        .in('id', selectedSubcontractorIds)

      if (subError) throw subError

      // Fetch assignments for these subcontractors in this week
      const { data: assignmentData, error: assignmentError } = await supabase
        .from('assignment_subcontractors')
        .select(`
          subcontractor_id,
          assignments!inner(
            id,
            assignment_number,
            title,
            start_date,
            start_time,
            end_time,
            estimated_hours,
            status
          )
        `)
        .in('subcontractor_id', selectedSubcontractorIds)
        .gte('assignments.start_date', startDate)
        .lte('assignments.start_date', endDate)

      if (assignmentError) throw assignmentError

      // Map data to our structure
      return subData?.map(sub => ({
        ...sub,
        assignments: assignmentData
          ?.filter(as => as.subcontractor_id === sub.id)
          .map(as => as.assignments as unknown as SubcontractorAssignment) || []
      })) as Subcontractor[]
    },
    enabled: isOpen && selectedSubcontractorIds.length > 0
  })

  // Fetch unavailability data for selected subcontractors
  const { data: unavailabilityData = [] } = useQuery({
    queryKey: ['calendar-panel-unavailability', selectedSubcontractorIds],
    queryFn: async () => {
      if (selectedSubcontractorIds.length === 0) return []
      const { data, error } = await supabase
        .from('subcontractor_unavailability')
        .select('*')
        .in('subcontractor_id', selectedSubcontractorIds)
      if (error) throw error
      return data as Unavailability[]
    },
    enabled: isOpen && selectedSubcontractorIds.length > 0
  })

  // Day abbreviations for mobile
  const dayAbbreviations = ['M', 'T', 'O', 'T', 'F', 'L', 'S']
  const dayNames = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön']

  // Calculate hours for a subcontractor on a given day
  const getHoursForDay = (subcontractor: Subcontractor, day: Date): number => {
    const dayStr = format(day, 'yyyy-MM-dd')
    return subcontractor.assignments
      .filter(a => a.start_date === dayStr)
      .reduce((sum, a) => {
        if (a.estimated_hours) return sum + a.estimated_hours
        // Calculate from times if no estimated hours
        if (a.start_time && a.end_time) {
          const [startH, startM] = a.start_time.split(':').map(Number)
          const [endH, endM] = a.end_time.split(':').map(Number)
          return sum + (endH - startH) + (endM - startM) / 60
        }
        return sum
      }, 0)
  }

  // Check if this assignment is on a specific day
  const isAssignmentOnDay = (day: Date): boolean => {
    if (!currentDate) return false
    return isSameDay(new Date(currentDate), day)
  }

  // Get assignments for a subcontractor on a given day
  const getAssignmentsForDay = (subcontractor: Subcontractor, day: Date): SubcontractorAssignment[] => {
    const dayStr = format(day, 'yyyy-MM-dd')
    return subcontractor.assignments
      .filter(a => a.start_date === dayStr)
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
  }

  // Check if a subcontractor is available on a given day
  // Returns { available: boolean, reason?: string }
  const checkSubcontractorAvailability = (subcontractor: Subcontractor, day: Date): { available: boolean; reason?: string } => {
    // Check unavailability periods first (they have specific reasons)
    const unavailabilityCheck = isDateInUnavailabilityPeriod(subcontractor.id, day, unavailabilityData)
    if (unavailabilityCheck.unavailable) {
      return { available: false, reason: unavailabilityCheck.reason }
    }

    // Check weekly availability
    if (!subcontractor.available_days) return { available: true }
    const jsDay = day.getDay() // 0=Sun, 1=Mon, ..., 6=Sat
    const dayIndex = jsDay === 0 ? 6 : jsDay - 1 // Convert to 0=Mon, 6=Sun
    const isAvailable = subcontractor.available_days[dayIndex] ?? true
    return { available: isAvailable }
  }

  // Format time for display (08:00 -> 08:00)
  const formatTime = (time: string | null): string => {
    if (!time) return ''
    return time.substring(0, 5)
  }

  // Navigation
  const goToPreviousWeek = () => setCurrentWeekStart(prev => subWeeks(prev, 1))
  const goToNextWeek = () => setCurrentWeekStart(prev => addWeeks(prev, 1))
  const goToToday = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))

  // Handle date selection
  const handleDateClick = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd')
    if (dateStr === currentDate) return // Already selected
    onDateChange(dateStr, isPastPreliminary)
  }

  // Toggle TG in filter
  const toggleSubcontractor = (id: string) => {
    setSelectedSubcontractorIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id)
      }
      return [...prev, id]
    })
  }

  // Check if a TG is assigned to this assignment
  const isAssignedToThisAssignment = (id: string) => assignedSubcontractorIds.includes(id)

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 md:bg-black/30"
        onClick={onClose}
      />

      {/* Panel - full screen on mobile, slide-in on desktop */}
      <div className={`
        fixed z-50 bg-[#faf9f7] dark:bg-nature-800 shadow-xl
        transition-transform duration-300 ease-out
        flex flex-col
        inset-x-0 top-0 bottom-0 md:inset-auto md:right-0 md:top-0 md:bottom-0 md:w-[500px] lg:w-[650px]
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Header - sticky on mobile */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-nature-200 dark:border-nature-700 bg-[#faf9f7] dark:bg-nature-800 sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-600" />
            <h2 className="font-semibold text-nature-900 dark:text-nature-100">
              Kalender
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 sm:p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors bg-nature-100 dark:bg-nature-700 md:bg-transparent"
            title="Stäng"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-nature-200 dark:border-nature-700 bg-nature-50 dark:bg-nature-900">
          <button
            onClick={goToPreviousWeek}
            className="p-2 hover:bg-nature-200 dark:hover:bg-nature-700 rounded-lg transition-colors min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="text-center">
            <button
              onClick={goToToday}
              className="text-sm font-semibold text-nature-900 dark:text-nature-100 hover:text-primary-600 dark:hover:text-primary-400"
            >
              Vecka {weekNumber}
            </button>
            <div className="text-xs text-nature-500">
              {format(currentWeekStart, 'd MMM', { locale: sv })} - {format(addDays(currentWeekStart, 6), 'd MMM yyyy', { locale: sv })}
            </div>
          </div>
          <button
            onClick={goToNextWeek}
            className="p-2 hover:bg-nature-200 dark:hover:bg-nature-700 rounded-lg transition-colors min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {/* Assignment Info */}
          <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
            <div className="font-medium text-primary-900 dark:text-primary-100 truncate">
              {assignmentTitle}
            </div>
            {currentDate && (
              <div className="text-sm text-primary-600 dark:text-primary-400 flex items-center gap-2 mt-1">
                <Clock className="h-3.5 w-3.5" />
                <span>{currentDate}</span>
              </div>
            )}
          </div>

          {/* TG Filter */}
          <div className="mb-4 relative">
            <button
              onClick={() => setShowTgFilter(!showTgFilter)}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm
                ${showTgFilter
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'border-nature-300 dark:border-nature-600 hover:border-nature-400 dark:hover:border-nature-500 text-nature-600 dark:text-nature-400'
                }
              `}
            >
              <Filter className="h-4 w-4" />
              <span>Trädgårdsmästare ({selectedSubcontractorIds.length})</span>
            </button>

            {/* Filter Dropdown */}
            {showTgFilter && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 z-10 max-h-64 overflow-y-auto">
                {isLoadingAllTg ? (
                  <div className="p-4 text-center text-nature-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto mb-2"></div>
                    <span className="text-sm">Laddar...</span>
                  </div>
                ) : allSubcontractors.length === 0 ? (
                  <div className="p-4 text-center text-nature-500 text-sm">
                    Inga aktiva trädgårdsmästare hittades
                  </div>
                ) : (
                  <div className="p-2">
                    {allSubcontractors.map(tg => {
                      const isSelected = selectedSubcontractorIds.includes(tg.id)
                      const isAssigned = isAssignedToThisAssignment(tg.id)
                      return (
                        <button
                          key={tg.id}
                          onClick={() => toggleSubcontractor(tg.id)}
                          className={`
                            w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors
                            ${isSelected
                              ? 'bg-primary-50 dark:bg-primary-900/20'
                              : 'hover:bg-nature-100 dark:hover:bg-nature-700'
                            }
                          `}
                        >
                          {/* Checkbox indicator */}
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                            ${isSelected
                              ? 'border-primary-500 bg-primary-500 text-white'
                              : 'border-nature-300 dark:border-nature-600'
                            }
                          `}>
                            {isSelected && <Check className="h-3 w-3" />}
                          </div>

                          {/* Color dot */}
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: tg.color }}
                          />

                          {/* Name */}
                          <span className="flex-1 text-nature-900 dark:text-nature-100 truncate">
                            {tg.name}
                          </span>

                          {/* Assigned badge */}
                          {isAssigned && (
                            <span className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 rounded">
                              <UserCheck className="h-3 w-3" />
                              Tilldelad
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 md:mb-3">
            {weekDays.map((day, index) => {
              const holidayInfo = getHolidayForDate(day)
              const isRedDay = holidayInfo?.isRedDay || false
              const isSunday = day.getDay() === 0
              const isSelected = isAssignmentOnDay(day)

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => handleDateClick(day)}
                  className={`
                    p-2 md:p-3 rounded-lg text-center transition-colors min-h-[60px] md:min-h-[70px]
                    ${isSelected
                      ? 'bg-primary-500 text-white ring-2 ring-primary-300'
                      : isToday(day)
                        ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-300 dark:ring-primary-700'
                        : isRedDay || isSunday
                          ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
                          : 'bg-nature-100 dark:bg-nature-700 hover:bg-nature-200 dark:hover:bg-nature-600'
                    }
                  `}
                >
                  {/* Day name - short on mobile, full on desktop */}
                  <div className={`text-xs md:text-sm font-medium ${
                    isSelected
                      ? 'text-white/80'
                      : isRedDay || isSunday
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-nature-500'
                  }`}>
                    <span className="md:hidden">{dayAbbreviations[index]}</span>
                    <span className="hidden md:inline">{dayNames[index]}</span>
                  </div>
                  {/* Day number and date */}
                  <div className={`text-lg md:text-xl font-bold ${
                    isSelected
                      ? 'text-white'
                      : isRedDay || isSunday
                        ? 'text-red-700 dark:text-red-300'
                        : 'text-nature-900 dark:text-nature-100'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  {/* Month on desktop */}
                  <div className={`hidden md:block text-[10px] ${
                    isSelected
                      ? 'text-white/70'
                      : 'text-nature-400'
                  }`}>
                    {format(day, 'MMM', { locale: sv })}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Subcontractor Rows */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : subcontractors.length === 0 ? (
            <div className="text-center py-8 text-nature-500">
              <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Inga trädgårdsmästare tilldelade</p>
            </div>
          ) : (
            <div className="space-y-2">
              {subcontractors.map(sub => {
                const isAssigned = isAssignedToThisAssignment(sub.id)
                return (
                <div
                  key={sub.id}
                  className="rounded-lg overflow-hidden border border-nature-200 dark:border-nature-700"
                  style={{ borderLeftColor: sub.color, borderLeftWidth: '4px' }}
                >
                  {/* Subcontractor Header */}
                  <div className="px-3 py-2 bg-nature-50 dark:bg-nature-700/50">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: sub.color }}
                      />
                      <span className="font-medium text-nature-900 dark:text-nature-100 truncate flex-1">
                        {sub.name}
                      </span>
                      {isAssigned ? (
                        <span className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 rounded flex-shrink-0">
                          <UserCheck className="h-3 w-3" />
                          <span className="hidden md:inline">Tilldelad</span>
                        </span>
                      ) : (
                        <button
                          onClick={() => onAddSubcontractor({ id: sub.id, name: sub.name, phone: sub.phone })}
                          className="flex items-center gap-1 text-xs text-nature-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30 px-2 py-0.5 rounded transition-colors flex-shrink-0"
                          title="Tilldela till detta uppdrag"
                        >
                          <UserPlus className="h-3 w-3" />
                          <span className="hidden md:inline">Tilldela</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Week Assignments Grid */}
                  <div className="grid grid-cols-7 gap-px bg-nature-200 dark:bg-nature-600">
                    {weekDays.map(day => {
                      const dayAssignments = getAssignmentsForDay(sub, day)
                      const totalHours = getHoursForDay(sub, day)
                      const isThisAssignmentDay = isAssignmentOnDay(day)
                      const availability = checkSubcontractorAvailability(sub, day)
                      const isAvailable = availability.available

                      // Build tooltip text
                      const tooltipText = !isAvailable
                        ? availability.reason
                          ? `Ej tillgänglig: ${availability.reason}`
                          : 'Ej tillgänglig denna dag'
                        : undefined

                      return (
                        <div
                          key={day.toISOString()}
                          className={`
                            p-1 md:p-2 min-h-[60px] md:min-h-[80px] flex flex-col relative
                            ${isThisAssignmentDay
                              ? 'bg-primary-50 dark:bg-primary-900/20'
                              : !isAvailable
                                ? 'bg-nature-100 dark:bg-nature-900'
                                : 'bg-white dark:bg-nature-800'
                            }
                          `}
                          title={tooltipText}
                        >
                          {/* Unavailable indicator */}
                          {!isAvailable && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-full h-[1px] bg-nature-300 dark:bg-nature-600 rotate-[-20deg]" />
                            </div>
                          )}
                          {dayAssignments.length > 0 ? (
                            <div className="space-y-1 flex-1 relative z-10">
                              {dayAssignments.map(assignment => {
                                const isThisAssignment = assignment.id === assignmentId
                                return (
                                  <div
                                    key={assignment.id}
                                    className={`
                                      px-1.5 py-1 rounded text-[10px] md:text-xs leading-tight
                                      ${isThisAssignment
                                        ? 'bg-primary-500 text-white font-medium'
                                        : 'bg-nature-200 dark:bg-nature-600 text-nature-600 dark:text-nature-300'
                                      }
                                    `}
                                    title={`${assignment.title}\n${formatTime(assignment.start_time)}-${formatTime(assignment.end_time)}`}
                                  >
                                    {/* Time - always show */}
                                    <div className="font-medium">
                                      {assignment.start_time && assignment.end_time ? (
                                        <span>{formatTime(assignment.start_time)}-{formatTime(assignment.end_time)}</span>
                                      ) : (
                                        <span className="italic">Ingen tid</span>
                                      )}
                                    </div>
                                    {/* Title - only on desktop */}
                                    <div className="hidden md:block truncate text-[10px] opacity-80">
                                      {isThisAssignment ? 'Detta uppdrag' : assignment.title}
                                    </div>
                                  </div>
                                )
                              })}
                              {/* Total hours */}
                              <div className="text-[10px] md:text-xs text-nature-400 text-center mt-auto pt-0.5">
                                Σ {totalHours.toFixed(1)}h
                              </div>
                            </div>
                          ) : (
                            <span className={`text-xs m-auto ${!isAvailable ? 'text-nature-300 dark:text-nature-600' : 'text-nature-400'}`}>
                              {!isAvailable ? '—' : '-'}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )})}
            </div>
          )}

        </div>

        {/* Footer with status indicator */}
        {isPastPreliminary && (
          <div className="px-4 py-3 border-t border-nature-200 dark:border-nature-700 bg-amber-50 dark:bg-amber-900/20">
            <p className="text-xs text-amber-700 dark:text-amber-300 text-center">
              Uppdraget är planerat - ändringar kräver bekräftelse
            </p>
          </div>
        )}
      </div>
    </>
  )
}
